import { Pencil } from 'lucide-react';
import { Button } from '../core/Button';
import type { WizardStep } from '../../types/wizard';
import { DEPARTMENTS } from '../../types/wizard';
import { useWizard } from '../../context/WizardContext';

interface ReviewRowProps {
  label: string;
  value: string;
  step: WizardStep;
}

function ReviewRow({ label, value, step }: Readonly<ReviewRowProps>) {
  const { goToStep } = useWizard();
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-700 last:border-0 gap-4">
      <div className="min-w-0">
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</p>
        <p className="font-medium text-white mt-0.5 text-sm truncate">{value}</p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => goToStep(step)}
        disabled
        className="shrink-0 text-slate-400"
      >
        <Pencil className="h-3 w-3" />
        Edit
      </Button>
    </div>
  );
}

export function ReviewCard() {
  const { state } = useWizard();

  function classValue(): string {
    if (state.selectedClass === 10) return 'SSC (Class 9–10)';
    if (state.selectedClass === 12) return 'HSC (Class 11–12)';
    return '—';
  }

  function examTypeValue(): string {
    if (state.selectedExamType === 'written') return 'Written';
    if (state.selectedExamType === 'mcq') return 'MCQ';
    return '—';
  }

  const rows: ReviewRowProps[] = [
    { label: 'Class', value: classValue(), step: 'class' },
    {
      label: 'Department',
      value: state.selectedDepartment
        ? (DEPARTMENTS.find((d) => d.id === state.selectedDepartment)?.name ?? '—')
        : '—',
      step: 'department',
    },
    { label: 'Subject', value: state.selectedSubject?.name ?? '—', step: 'subject' },
    { label: 'Paper', value: state.selectedPaper?.name ?? '—', step: 'paper' },
    {
      label: 'Chapters',
      value:
        state.selectedChapters.length > 0
          ? state.selectedChapters.map((c) => c.name).join(', ')
          : '—',
      step: 'chapter',
    },
    { label: 'Exam Type', value: examTypeValue(), step: 'examType' },
    {
      label: 'Questions',
      value: state.numberOfQuestions ? String(state.numberOfQuestions) : '—',
      step: 'questions',
    },
    { label: 'Duration', value: state.duration ? `${state.duration} min` : '—', step: 'questions' },
    {
      label: 'Total Marks',
      value: state.totalMarks ? String(state.totalMarks) : '—',
      step: 'questions',
    },
    { label: 'Version', value: state.selectedVersion ?? '—', step: 'version' },
  ];

  return (
    <div className="rounded-2xl border border-slate-700 bg-slate-800 overflow-hidden shadow-sm">
      <div className="px-5 py-4 border-b border-slate-700 bg-gradient-to-r from-violet-500/10 to-indigo-500/10">
        <h3 className="font-semibold text-white">Review your selections</h3>
        <p className="text-sm text-slate-400 mt-0.5">Everything look good? Generate when ready.</p>
      </div>
      <div className="px-5">
        {rows.map((row) => (
          <ReviewRow key={row.label} {...row} />
        ))}
      </div>
    </div>
  );
}

interface ReviewRowProps {
  label: string;
  value: string;
  step: WizardStep;
}
